import { useEffect, useMemo, useState } from "react";
import { useAccountJwtToken } from ".";
import { invokeGtag } from "../others";

/**
 * Use the `?magic-link-token=` parameter and create a new session (JWT).
 * It automatically returns the current JWT token if already one is set in current browser cache.
 */
function useAccountMagicLinkToken(receiveJwt, localStorageName) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [magicLinkToken, setMagicLinkToken] = useState(params.get("magic-link-token") || undefined);
  const {
    jwt,
    saveJwt
  } = useAccountJwtToken(localStorageName);

  // Listen to `?magic-link-token=`
  useEffect(() => {
    if (magicLinkToken) {
      const post = async () => {
        // Automatically logout
        saveJwt(undefined);

        // Replace current URL without GET parameter
        const newUrl = window.location.href.replace(/([?]?magic-link-token=\w{8}-\w{4}-\w{4}-\w{4}-\w{12}[&]?)/gm, "");
        window.history.replaceState({}, document.title, newUrl);
        try {
          saveJwt(await receiveJwt(magicLinkToken));
          invokeGtag(gtag => gtag("event", "login", {
            event_category: "engagement",
            event_label: "Customer center login"
          }));
        } finally {
          setMagicLinkToken(undefined);
        }
      };
      post();
    }
  }, []);
  return {
    magicLinkToken,
    // Only return the JWT if we do not try to login into another account
    jwt: magicLinkToken ? undefined : jwt,
    saveJwt,
    /**
     * This function allows you to completely remove all known JWT and magic link tokens
     * so the magic link form is shown up again. Useful, if the login expired.
     */
    erase: function () {
      saveJwt(undefined);
      setMagicLinkToken(undefined);
    }
  };
}
export { useAccountMagicLinkToken };